/**
 * The document-root migration — the ONE read-boundary site that normalizes a
 * scene document to the D8 shape (SCENE_ROOT_SPEC §2, §3).
 *
 * **The rule.** A Trace document has exactly one root node, the *document
 * root*: an ordinary authored node carrying what the file says about itself
 * (root behaviors, `active`, the pivot transform) in `node.extras.trace`. The
 * engine's implicit scene root is a runtime mount point — never authored,
 * never serialized, never a behavior target.
 *
 * Three shapes arrive that are not that, and each is repaired here, once,
 * before any SceneObject exists:
 *
 * | Input | What happens |
 * |---|---|
 * | **pre-D8 Trace document** (`scenes[0].extras.trace`, written 2026-09-06 → 09-13) | one root: the scene payload is FOLDED onto it — its behaviors appended after the root's own, `active` only if the root states none, `scripts` lifted to the document. 0 or ≥ 2 roots: a document root is synthesized to carry the payload. |
 * | **legacy `.tracex` / `.glxf`** with the synthetic `"Scene"` node | nothing: under D8 that node simply IS the document root, transform and all. No absorption, no name test, no shape test. |
 * | **foreign document** (no `extras.trace` anywhere) | multi-root is legal glTF and stays as parsed — except when the file is opened AS the scene, where every root is mounted under a synthesized root named after the file (§2). Placed, its roots become N children of the placement. |
 *
 * Nothing here touches the engine root, and nothing is copied off the document
 * onto the node that references it: a placement is a plain glTF instantiation
 * (§2.3).
 */

import type { NormalizedSceneDocument } from '../formats/gltf21/types';
import type { TraceSceneRootExtras } from '../formats/tracex/types';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { ParsedDocumentNode } from './parsedDocument';
import { Logger } from '../../utils/Logger';

/** What the migration decided, for the passes that run after it. */
export interface DocumentRootMigration {
    /**
     * The document root. Undefined only for a foreign multi-root document that
     * was not opened as the scene — the one shape D8 deliberately leaves alone.
     */
    root?: ParsedDocumentNode;
    /**
     * Its index in `document.nodes`, when it is a node the document actually
     * has. Undefined when the reader synthesized it: a synthesized root is not
     * in the document and takes no index there.
     */
    rootIndex?: number;
    /** True when the reader had to invent the root (§3 rows 1 and 3). */
    synthesized: boolean;
    /**
     * The document's script map (`@type` → path). From `extras.trace.scripts`,
     * or lifted from a pre-D8 `scenes[0].extras.trace.scripts`.
     */
    scripts?: Record<string, string>;
}

export interface DocumentRootOptions {
    /** The file the document came from — the fallback name for a synthesized root. */
    fileName?: string;
    /**
     * True when this document is being opened AS the scene. Only then does a
     * FOREIGN multi-root document get a root invented for it (§2); placed, it
     * keeps its roots and the placement is the handle.
     */
    openAsScene?: boolean;
}

/** A node's `extras.trace`, typed. */
type NodeTrace = { behaviors?: BehaviorData[]; active?: boolean | string } & Record<string, unknown>;

function nodeTrace(node: ParsedDocumentNode): NodeTrace | undefined {
    return (node.extras as { trace?: NodeTrace } | undefined)?.trace;
}

/** Write a node's `extras.trace` back, keeping every foreign key beside it. */
function setNodeTrace(node: ParsedDocumentNode, trace: NodeTrace): void {
    const extras = (node.extras as Record<string, unknown> | undefined) ?? {};
    node.extras = { ...extras, trace };
}

/** The document's own `extras.trace` (format version, name, scripts). */
function documentTrace(
    document: NormalizedSceneDocument,
): { name?: string; scripts?: Record<string, string> } | undefined {
    return (document.extras as { trace?: { name?: string; scripts?: Record<string, string> } } | undefined)?.trace;
}

/**
 * Whether this document is OURS — the question that decides whether a
 * multi-root file is broken or merely foreign.
 *
 * Any `extras.trace`, at any level, is the marker: the document's, the scene's
 * (pre-D8), or any node's. A file with none is a plain glTF from outside the
 * ecosystem and keeps glTF's freedoms.
 */
function isTraceDocument(document: NormalizedSceneDocument, nodes: readonly ParsedDocumentNode[]): boolean {
    if (documentTrace(document)) return true;
    const scene = document.scenes[document.scene] ?? document.scenes[0];
    if ((scene?.extras as { trace?: unknown } | undefined)?.trace) return true;
    return nodes.some(node => nodeTrace(node) !== undefined);
}

/** `chair.gltf` → `chair`; a URL's query and directories are dropped. */
function baseNameOf(fileName: string | undefined): string | undefined {
    if (!fileName) return undefined;
    const last = fileName.split('?')[0].split('#')[0].replace(/\\/g, '/').split('/').pop();
    if (!last) return undefined;
    const stem = last.replace(/\.[^.]+$/, '');
    return stem || undefined;
}

/** A name no node in the document already answers to. */
function uniqueName(candidate: string, nodes: readonly ParsedDocumentNode[]): string {
    const taken = new Set(nodes.map(n => n.name));
    if (!taken.has(candidate)) return candidate;
    let n = 2;
    while (taken.has(`${candidate}_${n}`)) n++;
    return `${candidate}_${n}`;
}

/**
 * Normalize a parsed document to exactly one root, in place.
 *
 * `nodes` is the parsed array (index-aligned with `document.nodes` on entry); a
 * synthesized root is UNSHIFTED onto it, so the document root is always
 * `nodes[0]` afterwards for a fold-free migration and the caller can address it
 * by identity. `document.nodes` is never re-indexed — a foreign extension or a
 * `KHR_interactivity` graph that stores node indices must still mean what it
 * said (CONTENT_MODEL_SPEC §3.6).
 */
export function migrateToDocumentRoot(
    document: NormalizedSceneDocument,
    nodes: ParsedDocumentNode[],
    options: DocumentRootOptions = {},
): DocumentRootMigration {
    const scene = document.scenes[document.scene] ?? document.scenes[0];
    const rootIndices = scene?.nodes ?? [];
    const sceneTrace = (scene?.extras as { trace?: TraceSceneRootExtras } | undefined)?.trace;
    const docTrace = documentTrace(document);
    const scripts = docTrace?.scripts ?? sceneTrace?.scripts;

    // The pre-D8 slot is consumed here and nowhere else: dropping it now is
    // what stops the foreign-passthrough writer (#834) putting it back.
    if (sceneTrace && scene?.extras) {
        const { trace: _trace, ...rest } = scene.extras;
        scene.extras = Object.keys(rest).length > 0 ? rest : undefined;
    }

    // ── One root: fold, and we are done ──────────────────────────────────
    if (rootIndices.length === 1) {
        const root = nodes[rootIndices[0]];
        if (root) {
            if (sceneTrace) foldSceneExtras(root, sceneTrace);
            return { root, rootIndex: rootIndices[0], synthesized: false, ...(scripts ? { scripts } : {}) };
        }
    }

    const ours = isTraceDocument(document, nodes);

    // ── A foreign document, placed: multi-root is legal and stays ─────────
    if (!ours && !options.openAsScene) {
        return { synthesized: false, ...(scripts ? { scripts } : {}) };
    }

    // ── 0 or ≥ 2 roots: synthesize the root the document is missing ───────
    const name = uniqueName(
        scene?.name ?? docTrace?.name ?? baseNameOf(options.fileName) ?? 'Scene',
        nodes,
    );
    const root: ParsedDocumentNode = { name };
    if (sceneTrace) {
        const trace: NodeTrace = {};
        if (sceneTrace.behaviors?.length) trace.behaviors = sceneTrace.behaviors;
        if (sceneTrace.active !== undefined) trace.active = sceneTrace.active;
        if (Object.keys(trace).length > 0) setNodeTrace(root, trace);
    }

    for (const index of rootIndices) {
        const node = nodes[index];
        if (node) {
            node.parentName = name;
            node.parentNode = root;
        }
    }
    nodes.unshift(root);

    Logger.log(
        `[documentRoot] ${ours ? 'a Trace' : 'a foreign'} document with ${rootIndices.length} root node(s) `
        + `was normalized to one: "${name}" (SCENE_ROOT_SPEC §3). The next save writes D8 shape.`,
    );

    return { root, synthesized: true, ...(scripts ? { scripts } : {}) };
}

/**
 * Fold a pre-D8 `scenes[0].extras.trace` onto the document root (§3, row 1).
 *
 * Order is the whole point: the root's OWN behaviors come first and the scene's
 * follow, so a type the root already declares keeps winning over the scene's
 * copy of it. `active` is taken only when the root states none — the node is
 * the authority on itself.
 */
function foldSceneExtras(root: ParsedDocumentNode, sceneTrace: TraceSceneRootExtras): void {
    const own = nodeTrace(root) ?? {};
    const behaviors = [...(own.behaviors ?? []), ...(sceneTrace.behaviors ?? [])];
    const trace: NodeTrace = { ...own };
    if (behaviors.length > 0) trace.behaviors = behaviors;
    if (own.active === undefined && sceneTrace.active !== undefined) trace.active = sceneTrace.active;
    setNodeTrace(root, trace);

    if (sceneTrace.behaviors?.length || sceneTrace.active !== undefined) {
        Logger.log(
            `[documentRoot] folded a pre-D8 scene payload onto "${root.name}" `
            + `(${sceneTrace.behaviors?.length ?? 0} behavior(s)) — SCENE_ROOT_SPEC §3.`,
        );
    }
}
